import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { linear } from '../chart-scales';
import type { CohortModel } from '../analytics-charts.model';
import { COHORT_COLORS, COHORT_SHAPES } from '../chart-palette';
import { useElementWidth } from '../use-element-width';
import { Marker, pctText } from './svg-parts';

// c) Egreso acumulado por semestre desde el ingreso, una línea por ventana de 5
// años. Cada curva termina donde termina su observación: no se prolonga ni baja
// a cero. Las suprimidas no se dibujan (están en la leyenda, con su rótulo).
// Una curva incompleta termina en un marcador HUECO; su estado va en la leyenda
// (los rótulos sobre el gráfico se pisaban con las curvas cortas).

export function cohortLabel(
  key: string,
  fromYear: number | null,
  toYear: number | null,
  beforeText: string,
) {
  return key === 'before' ? beforeText : `${fromYear}–${toYear}`;
}

export function CohortChart({ model, startYear }: { model: CohortModel; startYear: number }) {
  const { t } = useTranslation('sis');
  const theme = useTheme();
  const mode = theme.palette.mode;
  const { ref, width: W } = useElementWidth<HTMLDivElement>();
  const narrow = W < 560;
  const ink = theme.palette.text.secondary;
  const grid = theme.palette.divider;
  const paper = theme.palette.background.paper;
  const m = { l: 44, r: 16, t: 20, b: 44 };
  const H = narrow ? 300 : 340;
  const K = model.horizon;
  const x = linear(1, K, m.l + 6, W - m.r - 6);
  const y = linear(0, 1, H - m.b, m.t);
  const before = t('reports.analytics.cohorts.before', { year: startYear });
  // Las incompletas, encima: que no queden tapadas.
  const drawn = model.lines
    .filter((l) => l.points && l.points.length > 0)
    .sort((a, b) => b.observedTerms - a.observedTerms);

  return (
    <div ref={ref}>
      <svg
        width="100%"
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={t('reports.analytics.cohorts.title')}
      >
        {[0, 0.25, 0.5, 0.75, 1].map((v) => (
          <g key={v}>
            <line x1={m.l} x2={W - m.r} y1={y(v)} y2={y(v)} stroke={grid} />
            <text x={m.l - 6} y={y(v) + 4} fontSize={11} textAnchor="end" fill={ink}>
              {pctText(v)}
            </text>
          </g>
        ))}
        {Array.from({ length: K }, (_, i) => i + 1)
          .filter((k) => !narrow || k === 1 || k % 2 === 0)
          .map((k) => (
            <text key={k} x={x(k)} y={H - m.b + 16} fontSize={11} textAnchor="middle" fill={ink}>
              {k}
            </text>
          ))}
        <text x={(m.l + W - m.r) / 2} y={H - 8} fontSize={11.5} textAnchor="middle" fill={ink}>
          {t('reports.analytics.cohorts.x')}
        </text>
        <line
          x1={x(model.normalTerms)}
          x2={x(model.normalTerms)}
          y1={m.t - 6}
          y2={H - m.b}
          stroke={theme.palette.text.secondary}
          strokeWidth={1.25}
          strokeDasharray="4 4"
        />
        <text x={x(model.normalTerms) + 6} y={m.t + 4} fontSize={11} fontWeight={600} fill={ink}>
          {t('reports.analytics.cohorts.ref', { count: model.normalTerms })}
        </text>
        {drawn.map((l) => {
          const col = COHORT_COLORS[mode][l.slot % 8]!;
          const shape = COHORT_SHAPES[l.slot % 8]!;
          const pts = l.points!.map((v, i) => [x(i + 1), y(v)] as const);
          const d = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0]},${p[1]}`).join('');
          const name = cohortLabel(l.key, l.fromYear, l.toYear, before);
          return (
            <g key={l.key}>
              <path d={d} fill="none" stroke={paper} strokeWidth={5} strokeLinejoin="round" />
              <path d={d} fill="none" stroke={col} strokeWidth={2} strokeLinejoin="round" />
              {pts.map((p, i) =>
                i === pts.length - 1 || (i + 1) % 2 === 0 ? (
                  <Marker
                    key={i}
                    shape={shape}
                    x={p[0]}
                    y={p[1]}
                    color={col}
                    ring={paper}
                    r={i === pts.length - 1 ? 4.5 : 3.5}
                    hollow={!l.complete && i === pts.length - 1}
                  />
                ) : null,
              )}
              <title>{`${name}: ${l.points!.map((v, i) => `${i + 1}→${pctText(v)}`).join(' · ')}`}</title>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
